import { AxiosResponse } from 'axios';
import config from 'config';
import { ListPayload, SuccessResponse } from 'core/http/response';
import {
    CreateUpdateTeamPayload,
    SingleTeamResponse,
    GetTeamsParams,
    ListTeamsResponse,
    Team,
    AddTeamMemberPayload,
} from './types';
import axiosClient from '../../config/axios';
import { Customer, CustomerParams } from 'store/customers/customer';
import { Invoice, InvoicesStatuses, ListInvoicesParams } from 'store/invoices/types';
import { listManifests } from 'store/manifests/client';
import { Manifest } from 'store/manifests/types';
import { listJobs } from 'store/jobs/client';
import { JobResponse } from 'store/jobs/job';

const baseUrl = `${config.cmsUrl}/teams`;

export const getTeamById = async (teamId: string): Promise<Team> => {
    const response: AxiosResponse<SingleTeamResponse> = await axiosClient.get(`${baseUrl}/${teamId}`);
    return response.data.payload;
};

export const updateTeam = async (teamId: string, payload: CreateUpdateTeamPayload): Promise<Team> => {
    const response: AxiosResponse<SingleTeamResponse> = await axiosClient.put(`${baseUrl}/${teamId}`, payload);
    return response.data.payload;
};

export const createTeam = async (payload: CreateUpdateTeamPayload): Promise<Team> => {
    const response: AxiosResponse<SingleTeamResponse> = await axiosClient.post(baseUrl, payload);
    return response.data.payload;
};

export const removeIndividualFromTeam = async (teamId: string, individualId: string): Promise<Team> => {
    const response: AxiosResponse<SingleTeamResponse> = await axiosClient.patch(
        `${baseUrl}/${teamId}/remove-member/${individualId}`
    );
    return response.data.payload;
};

export const addIndividualToTeam = async ({ teamId, individualId, role }: AddTeamMemberPayload): Promise<Team> => {
    const response: AxiosResponse<SingleTeamResponse> = await axiosClient.patch(
        `${baseUrl}/${teamId}/add-member/${individualId}`,
        {},
        { params: { role } }
    );
    return response.data.payload;
};

export const activateTeam = async (teamId: string): Promise<Team> => {
    const response: AxiosResponse<SingleTeamResponse> = await axiosClient.patch(`${baseUrl}/${teamId}/activate`);
    return response.data.payload;
};

export const deactivateTeam = async (teamId: string): Promise<Team> => {
    const response: AxiosResponse<SingleTeamResponse> = await axiosClient.patch(`${baseUrl}/${teamId}/deactivate`);
    return response.data.payload;
};

export const listTeams = async (params?: GetTeamsParams & { signal?: AbortSignal }): Promise<ListPayload<Team>> => {
    const { signal, ...restParams } = params ?? {};
    const response: AxiosResponse<ListTeamsResponse> = await axiosClient.get(`${baseUrl}/list`, {
        params: restParams,
        signal,
    });
    return response.data.payload;
};

export const listCustomersOfTeam = async (
    teamId: string,
    params?: Partial<CustomerParams>,
    options?: { signal?: AbortSignal }
): Promise<ListPayload<Customer>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<Customer>>> = await axiosClient.get(
        `${baseUrl}/${teamId}/list/customers`,
        {
            params,
            signal: options?.signal,
        }
    );
    return response.data.payload;
};

export const listTeamInvoices = async (teamId: string, params?: ListInvoicesParams): Promise<ListPayload<Invoice>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<Invoice>>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/team/${teamId}`,
        { params }
    );
    return response.data.payload;
};

export const listTeamManifests = (teamId: string): Promise<ListPayload<Manifest>> =>
    listManifests({ teamId, archived: false, size: 1 });

export const listTeamJobs = (teamId: string): Promise<ListPayload<JobResponse>> =>
    listJobs({ teamId, archived: false, size: 1 });

export const listTeamInvoicesCounts = async (
    teamId: string,
    archived: boolean
): Promise<ListPayload<{ status: InvoicesStatuses; count: number }>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<{ status: InvoicesStatuses; count: number }>>> =
        await axiosClient.get(`${config.declarationsUrl}/invoice/status-count/team/${teamId}`, {
            params: {
                archived,
            },
        });
    return response.data.payload;
};
